'use client';

import { Box, Divider, Stack, Typography } from "@mui/material";
import { Markdown } from "@/app/features/shared/markdown/Markdown";
import { RelatedInfo } from "@/app/lib/generated/graphql-types";
import { notNull } from "@/app/lib/common/not-null";
import { MediaRef } from "@/app/__to_be_integrated/_____components/image/enhanced-image/types/types";
import MediaView from "@/app/features/shared/media-view/ui/public/MediaView.public";
import ContactView from "@/app/features/shared/related-infos/ui/ContactView";
import { RELATED_INFOS_SPACING } from "@/app/features/shared/related-infos/ui/spacing";
import Section from "@/app/features/shared/snap-section/Section";
import LinkButton from "@/app/features/shared/__to_be_integrated/LinkButton";

export default function RelatedInfoViewPersonalContact({ info }: { info: RelatedInfo }) {
    const {
        image,
        sideImage,
        showGeneralContact,
        teamMember,
        title,
        description,
    } = info;

    const hasNoContent = !title?.trim() && !description?.trim();

    let imageColumns: number = !!image ? 3 : 0;
    let contentColumns: number = hasNoContent ? 0 : 12;
    let rightSiteColumns: number = 0;

    if (image) {
        contentColumns -= 3;
    }
    if (!!teamMember || showGeneralContact) {
        contentColumns -= 3;
        rightSiteColumns += 3;
    }

    if (contentColumns <= 0) {
        contentColumns = 0;
        imageColumns = 12 - rightSiteColumns;
    }

    return (
        <Section contentHeight="100svh">
            {/* DESKTOP */}
            <span className="hidden md:block">
                <Stack className="related-info-personal-contact-view-root hidden md:block">
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
                            gap: RELATED_INFOS_SPACING.layout,
                            gridAutoRows: "calc(100svh - var(--header-height))",
                        }}
                    >
                        {image && (
                            <Box
                                sx={{
                                    gridColumn: `span ${imageColumns}`,
                                    alignContent: "center",
                                    minHeight: 0,
                                    height: "100%",
                                    boxSizing: "border-box",
                                    py: RELATED_INFOS_SPACING.columnY,
                                }}
                            >
                                <ImageView image={image} />
                            </Box>
                        )}
                        {contentColumns > 0 && (
                            <Box
                                sx={{
                                    gridColumn: `span ${contentColumns}`,
                                    minHeight: 0,
                                    height: "100%",
                                    boxSizing: "border-box",
                                    py: RELATED_INFOS_SPACING.columnY
                                }}
                            >
                                <TextView info={info} />
                            </Box>
                        )}
                        {rightSiteColumns > 0 && (
                            <Box
                                sx={{
                                    gridColumn: `span ${rightSiteColumns}`,
                                    minHeight: 0,
                                    height: "100%",
                                    boxSizing: "border-box",
                                    py: RELATED_INFOS_SPACING.columnY,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: RELATED_INFOS_SPACING.content,
                                }}
                            >
                                {(!!teamMember || showGeneralContact) &&
                                    <Box
                                        sx={{
                                            minHeight: 0,
                                            maxHeight: "100%",
                                            "& .team-member-root": {
                                                height: "100%",
                                                minHeight: 0,
                                                display: "flex",
                                                flexDirection: "column",
                                            },
                                            "& .team-member-root > :first-of-type": {
                                                flex: "1 1 auto",
                                                minHeight: 96,
                                            },
                                        }}
                                    >
                                        <ContactView info={info} />
                                    </Box>
                                }
                                {sideImage && <ImageView image={sideImage} />}
                            </Box>
                        )}
                    </Box>
                    <Box sx={{ py: RELATED_INFOS_SPACING.dividerY }}><Divider /></Box>
                </Stack>
            </span>
            {/* MOBILE */}
            <span className="block md:hidden">
                <Stack
                    className="related-info-personal-contact-view-root block md:hidden"
                    spacing={RELATED_INFOS_SPACING.mobile}
                >
                    {image && <ImageView image={image}/>}
                    <TextView info={info} />
                    {sideImage && <ImageView image={sideImage}/>}
                    {(!!teamMember || showGeneralContact) &&
                        <ContactView info={info} />
                    }

                </Stack>
            </span>
        </Section>
    )
}

function TextView({ info }: { info: RelatedInfo }) {
    const {
        title,
        subtitle,
        description,
        links
    } = info;
    const normalizedTitle = title?.trim();
    const normalizedSubtitle = subtitle?.trim();

    return (
        <Stack spacing={RELATED_INFOS_SPACING.content} justifyContent="center" className="h-full">
            <Stack spacing={RELATED_INFOS_SPACING.text}>
                {normalizedSubtitle && (
                    <Typography variant="subtitle2">{normalizedSubtitle}</Typography>
                )}
                {normalizedTitle && (
                    <Typography variant="h2">{normalizedTitle}</Typography>
                )}
                {description && (
                    <Markdown value={description}/>
                )}
            </Stack>
            {links && links.length > 0 && (
                <Stack spacing={RELATED_INFOS_SPACING.content}>
                    <Stack spacing={RELATED_INFOS_SPACING.text} className="w-full">
                        {links.filter(notNull).map((link, index) => (
                            <LinkButton key={index} link={link} />
                        ))}
                    </Stack>
                </Stack>
            )}
        </Stack>
    )
}

function ImageView({ image }: { image: MediaRef }) {
    return (
        <MediaView
            src={image}
            fill
            magnifier="modal"
            objectPosition="center"
            objectFit="contain"
        />
    );
}
